import type { ExerciseTemplateSimplifyDTO } from "types/schemas";
import { useTPMEditorStore } from "../store/useTherapyPlanManagementEditorStore";
import { type SharedFormatMessage, usePatchFavouriteExercises } from "@akina/infra";
import { twMerge } from "tailwind-merge";
import { Icon, Text } from "../../atoms";
import { TPM_COPY_PREFIX } from "../therapyPlanManagementTypes";
import { useTherapyPlanManagementViewStore } from "../store/useTherapyPlanManagementViewStore";
import { ExerciseThumbnail } from "./ExerciseThumbnail";

const ALREADY_SELECTED_COPY = { id: `${TPM_COPY_PREFIX}.addRoutineAlreadySelected` } as const;
const EXERCISE_CARD_PROPS = {
    className: twMerge(
        "relative grid w-full grid-rows-[120px_68px] rounded-xl shadow-gray-800/40 transition-colors",
        "exercise-card-shadow",
    ),

}
const FAVORITE_ICON_PROPS = {
    className: "absolute top-[10px] left-[10px] z-20 flex h-[24px] w-[24px] scale-100 cursor-pointer items-center justify-center rounded-full transition-transform hover:scale-95 active:scale-80",
} as const

export function Exercise({ exercise, formatMessage }: Readonly<{ exercise: ExerciseTemplateSimplifyDTO; formatMessage: SharedFormatMessage }>) {
    const { setAddRoutineParams } = useTPMEditorStore(state => state.actions);
    const addRoutineParams = useTPMEditorStore(state => state.addRoutineParams);
    const selectedRoutines = addRoutineParams?.selectedRoutines ?? [];
    const disabled = selectedRoutines.some(r => r.exerciseTemplateKey === exercise.key);

    const handleClick = () => {
        if (disabled) return;
        setAddRoutineParams({ exerciseTemplateKey: exercise.key, exerciseName: exercise.displayedName });
    };

    return (
        <button type="button" {...EXERCISE_CARD_PROPS} onClick={handleClick} >
            <FavouriteExerciseButtonToggle exercise={exercise} />
            {disabled ? <AlreadySelectedExerciseOverlay formatMessage={formatMessage} /> : null}
            <ExerciseThumbnail thumbnail={exercise.thumbnail} previewGif={exercise.previewGif} />
            <div className="p-4"><ExerciseName name={exercise.displayedName} /></div>
        </button>
    );
}

export function ExerciseName({ name, className }: Readonly<{ name?: string; className?: string }>) {
    return (
        <div className={twMerge("flex h-full flex-1 flex-col gap-2", className)}>
            <Text size="sm" className="line-clamp-2 min-h-11 text-left leading-[22px] font-bold text-wrap wrap-anywhere hyphens-auto">
                {name ?? ""}
            </Text>
        </div>
    );
}

export function AlreadySelectedExerciseOverlay({ className, formatMessage }: Readonly<{ className?: string; formatMessage: SharedFormatMessage }>) {
    return (
        <div className="absolute top-0 left-0 z-10 flex w-full items-center justify-center h-[120px] rounded-t-lg bg-gray-100/60">
            <div className={twMerge("w-auto rounded bg-gray-100 px-2 py-[6px]", className)}>
                <Text className="leading-4" size="xs" color="text-gray-800/90">
                    {formatMessage(ALREADY_SELECTED_COPY)}
                </Text>
            </div>
        </div>
    );
}


function FavouriteExerciseButtonToggle({ exercise }: Readonly<{ exercise: ExerciseTemplateSimplifyDTO }>) {
    const favouriteExercises = useTherapyPlanManagementViewStore(state => state.favouriteExercises);
    const setFavouriteExercises = useTherapyPlanManagementViewStore(state => state.actions.setFavouriteExercises);
    const isFavourite = favouriteExercises.has(exercise.key);
    const { mutate: patchFavouriteExercises } = usePatchFavouriteExercises();

    const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
        e.stopPropagation();
        e.preventDefault();
        const prev = Array.from(favouriteExercises);
        const payload = Array.from(favouriteExercises);
        if (isFavourite) {
            payload.splice(payload.indexOf(exercise.key), 1);
        } else {
            payload.push(exercise.key);
        }
        setFavouriteExercises(payload);
        patchFavouriteExercises(payload, {
            onError: () => {
                setFavouriteExercises(prev);
            },
        });
    };

    return (
        <button
            type="button"
            {...FAVORITE_ICON_PROPS}
            onClick={e => handleClick(e)}
            aria-pressed={isFavourite}>
            {isFavourite ? (
                <Icon type="star" className="text-primary-500 h-[18px] w-[18px]" />
            ) : (
                <Icon type="starOutline" className="text-primary-50 h-[18px] w-[18px]" />
            )}
        </button>
    );
}

